<script lang="ts">
  /**
   * F26 · The estate explains itself to a stranger. One page, printable, current.
   *
   * The reader is somebody standing in the house who does not live in it: a
   * house-sitter, a grandparent, an emergency. That reader has no account, is not
   * going to be given one, and may well be holding a piece of paper rather than a
   * screen. Three consequences run through everything below:
   *
   *  · **It prints, properly.** A print stylesheet, not a screenshot — because a
   *    screenshot is out of date the moment a portal moves and nobody re-takes it.
   *    `@page` sets A4 and the type drops to a print scale so the whole thing lands
   *    on one sheet. Everything that is furniture on screen is `display: none` on
   *    paper.
   *  · **It is generated from the registry**, so it cannot drift. The instruction
   *    was "kept current automatically", and the only way that is true is if nobody
   *    has to remember to update it: add a portal in Fabric and it appears here at
   *    the next load, with its own description and its own address.
   *  · **It does not render outside `Shell`** by accident. This is a document, not a
   *    screen. Reaching into another repo's DOM from a print stylesheet to hide a
   *    rail and a header would work until somebody renamed a class there, and the
   *    failure would be silent and only visible on paper. A document renders as a
   *    document.
   *
   * What is *not* printed is as considered as what is. Health state is deliberately
   * absent: a sheet on a fridge saying a portal is live is a claim that goes stale
   * within the hour, and a stranger cannot tell a printed claim from a live one. The
   * live view is the Front Door's Status page and the page says so.
   */
  import { Icon, Panel, DASH } from "@steyncd/steyn-design";
  import { estate, host, ICON, type Portal } from "../lib/estate.svelte";
  import { CURATED } from "../lib/dailyline";

  /**
   * Live portals only, in rail order.
   *
   * A stranger cannot use something that is still being built, and a row for it is a
   * door painted on a wall. The count of the rest is stated in one line instead.
   */
  const listed = $derived(
    [...estate.portals].filter((p) => p.status === "live" && p.id !== "frontdoor").sort((a, b) => a.order - b.order),
  );
  const unbuilt = $derived(estate.portals.filter((p) => p.status !== "live").length);

  /**
   * "Where the documents are" is answerable from the registry rather than authored,
   * which is the whole point of F26: Vault is a registry entry with an address, and
   * if it ever moves this line moves with it. Null when Vault is not in the caller's
   * registry — in which case the section says so rather than naming a portal that
   * this reader cannot open.
   */
  const vault = $derived(listed.find((p) => p.id === "vault") ?? null);

  /** Who each role is, in the household's own words rather than the claim's. */
  const WHO: Record<string, string> = {
    owner: "Christo",
    partner: "Christo and Mandri",
    display: "anyone in the house, including the kitchen tablet",
    guest: "anyone given a guest pass, including a house-sitter",
  };

  /** Printed on the sheet, so a paper copy can be dated at a glance. */
  const printedOn = new Intl.DateTimeFormat("en-ZA", {
    timeZone: "Africa/Johannesburg",
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date());
</script>

<article class="doc">
  <!-- Screen-only controls. Not a header — the document's own title is below, and it
       is the same on screen as on paper. -->
  <nav class="bar">
    <a class="bar__back" href="#/"><Icon name="chevron-right" size={14} /> Back to the Front Door</a>
    <button class="btn-primary" onclick={() => window.print()}>
      <Icon name="printer" size={14} /> Print this page
    </button>
  </nav>

  <header class="head">
    <h1>The Steyn estate, on one page</h1>
    <p class="lede">
      If you are looking after the house and something needs doing, this is where each
      thing lives. Everything below is generated from the estate's own registry on the
      day it is printed, so it is current rather than remembered.
    </p>
    <p class="dated">Printed {printedOn}</p>
  </header>

  <section>
    <h2>What each portal is</h2>
    <Panel
      loading={estate.loading && !estate.loaded}
      error={estate.error}
      isEmpty={estate.loaded && listed.length === 0}
      onretry={() => estate.load()}
      rows={5}
    >
      {#snippet empty()}
        The registry answered with no portals you may open. That is an access question,
        not an empty estate — ask Christo to add your account in Fabric → People &amp;
        portals, then reload this page.
      {/snippet}

      <table>
        <thead>
          <tr>
            <th>Portal</th>
            <th>What it is</th>
            <th>Address</th>
            <th>Who may open it</th>
          </tr>
        </thead>
        <tbody>
          {#each listed as p (p.id)}
            <tr>
              <td class="nm">
                <!-- Glyphs from Icon only. The same mark appears in the rail, so the
                     printed page and the screen teach each other. -->
                <Icon name={ICON[p.id] ?? "door"} size={13} />
                {p.name}
              </td>
              <td>{p.blurb}</td>
              <td class="addr num">{host(p.url)}</td>
              <td>{WHO[p.minRole] ?? p.minRole}</td>
            </tr>
          {/each}
        </tbody>
      </table>

      {#if unbuilt > 0}
        <p class="note">
          {unbuilt}
          {unbuilt === 1 ? "portal is" : "portals are"} still being built and are not listed —
          there is nothing to open yet.
        </p>
      {/if}
    </Panel>
  </section>

  <div class="two">
    <section>
      <h2>Where the documents are</h2>
      {#if vault}
        <p>
          Everything the house has on paper is in <strong>{vault.name}</strong>, at
          <span class="num">{host(vault.url)}</span> — {vault.blurb.toLowerCase()}.
        </p>
      {:else}
        <p>
          {DASH} Vault is not in the registry this account can read, so this page will not
          name an address it cannot verify.
        </p>
      {/if}
      <!-- A labelled slot, not a guess. Where the physical folder lives is a fact
           about a room, and no system in the estate holds it. -->
      <p class="slot">
        <span class="slot__l">The paper in-case folder</span>
        {DASH} nothing records where it is kept.
        <span class="next">Write it on this line by hand, or store it in Vault so it prints here.</span>
      </p>
    </section>

    <section>
      <h2>Who to call</h2>
      <!-- The single largest gap in F26, stated rather than filled.
           This was checked rather than assumed: the registry has no contacts field,
           and `GET /users` holds sign-in accounts, not people to phone. Inventing a
           plausible plumber on a page a stranger will trust in an emergency is the
           worst thing this repo could ship. -->
      <p class="slot">
        <span class="slot__l">Emergency, medical, and the trades</span>
        {DASH} the estate stores no contacts. Nothing here has been guessed.
        <span class="next">
          Add a contacts collection to Fabric and this section fills itself from it, the
          same way the table above does.
        </span>
      </p>
      <p>
        Until then: everyone who can open something above can also be reached the usual
        way, and the Front Door's <strong>Estate</strong> page says who may open what.
      </p>
    </section>
  </div>

  <section>
    <h2>If something looks wrong</h2>
    <p>
      Nothing on this sheet says whether a portal is working, on purpose — a printed
      page cannot know. The live answer is the Front Door's <strong>Status</strong> page,
      which checks every portal every five minutes and names the ones that did not
      answer.
    </p>
    <p>
      The kitchen tablet shows the day without asking anyone to sign in. Its daily line
      comes from {CURATED} verses kept in the Front Door itself, so it works with no
      signal at all.
    </p>
  </section>

  <footer class="foot">
    The Steyn household · generated from the estate registry · {printedOn}
  </footer>
</article>

<style>
  /* ── On screen ───────────────────────────────────────────────────────────── */
  .doc {
    display: flex;
    flex-direction: column;
    gap: var(--sp-5);
    max-width: 900px;
    min-height: 100vh;
    margin: 0 auto;
    padding: var(--sp-5) var(--sp-4) 60px;
    background: var(--aurora);
    color: var(--tx);
  }

  .bar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: var(--sp-3);
  }
  .bar__back,
  .btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .bar__back {
    /* The glyph is a right chevron rotated, rather than a second glyph added to the
       map for one use. Icon.svelte owns the shapes; this owns the rotation. */
    color: var(--mut);
    font-size: var(--fs-small);
    font-weight: 700;
  }
  .bar__back :global(svg) {
    transform: rotate(180deg);
  }
  .bar__back:hover {
    color: var(--acc);
  }

  h1 {
    margin: 0;
    font-size: var(--fs-h1);
    font-weight: 800;
    letter-spacing: -0.6px;
  }
  .lede {
    margin: 10px 0 0;
    max-width: 68ch;
    font-size: var(--fs-body);
    line-height: 1.55;
    color: var(--tx2);
  }
  .dated {
    margin: 8px 0 0;
    font-size: var(--fs-micro);
    font-weight: 700;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: var(--mut);
  }

  h2 {
    margin: 0 0 8px;
    font-size: var(--fs-micro);
    font-weight: 800;
    letter-spacing: 0.6px;
    text-transform: uppercase;
    color: var(--mut);
  }
  section p {
    margin: 0 0 8px;
    font-size: var(--fs-small);
    line-height: 1.55;
    color: var(--tx2);
  }

  .two {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: var(--sp-5);
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--fs-small);
  }
  th {
    padding: 6px 10px 6px 0;
    text-align: left;
    font-size: var(--fs-micro);
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--mut);
    border-bottom: 1px solid var(--line);
  }
  td {
    padding: 8px 10px 8px 0;
    border-bottom: 1px solid var(--line);
    vertical-align: top;
    color: var(--tx2);
  }
  .nm {
    display: flex;
    align-items: center;
    gap: 7px;
    font-weight: 700;
    white-space: nowrap;
    color: var(--tx);
  }
  .addr {
    color: var(--tx2);
  }
  .note {
    margin: 8px 0 0;
    font-size: var(--fs-micro);
    color: var(--mut);
  }

  .slot {
    margin: 0 0 8px;
    font-size: var(--fs-small);
    line-height: 1.55;
    color: var(--mut);
  }
  .slot__l {
    font-weight: 800;
    color: var(--tx2);
  }
  .next {
    display: block;
    margin-top: 4px;
    font-weight: 700;
    color: var(--tx2);
  }

  .foot {
    margin-top: auto;
    padding-top: 12px;
    border-top: 1px solid var(--line);
    font-size: var(--fs-micro);
    color: var(--mut);
  }

  @media (max-width: 720px) {
    .two {
      grid-template-columns: 1fr;
    }
  }

  /* ── On paper ─────────────────────────────────────────────────────────────
     One A4 sheet is the acceptance criterion, so this is a real layout rather than
     "hide a few things". Three rules do most of the work:

       · Ink is black on white regardless of theme. `daylight` would nearly print,
         and the nine dark themes would put a page of toner on the paper. Colour is
         not carrying any meaning here — no status is printed — so dropping it costs
         nothing and saves the sheet.

         These four literals are the only hexes in this repo that are not tokens,
         and the reason is that paper has no ramp: there is no theme to honour and
         no `daylight` variant of a sheet of A4. They were computed rather than
         picked — #000 on #fff is 21:1 and the #222 lede is 15.9:1, both far past
         the 4.5:1 body-text threshold; #999 and #bbb are hairlines and carry no
         text and no meaning, so the text rule does not apply to them.
       · Type is in pt, and the floor is 9pt, which is 12px — above the estate's
         11.5px floor rather than sneaking under it in a unit nobody checks.
       · `break-inside: avoid` on every section, so if the registry grows past one
         page the break lands between sections rather than through a table row. */
  @media print {
    @page {
      size: A4 portrait;
      margin: 14mm;
    }

    .doc {
      max-width: none;
      min-height: 0;
      margin: 0;
      padding: 0;
      gap: 9pt;
      background: #fff;
      color: #000;
    }
    /* The buttons are the only things on the page that cannot be used on paper. */
    .bar {
      display: none;
    }

    h1 {
      font-size: 17pt;
      letter-spacing: -0.4pt;
    }
    .lede {
      max-width: none;
      font-size: 9.5pt;
      line-height: 1.4;
      color: #222;
    }
    .dated,
    h2,
    .note,
    .foot {
      font-size: 9pt;
      color: #000;
    }
    h2 {
      margin-bottom: 4pt;
      padding-bottom: 2pt;
      border-bottom: 0.5pt solid #999;
    }
    section p,
    .slot,
    table,
    td,
    th {
      font-size: 9pt;
      line-height: 1.35;
      color: #000;
    }
    th,
    td {
      padding: 3pt 8pt 3pt 0;
      border-bottom: 0.5pt solid #bbb;
    }
    .slot__l,
    .next,
    .addr {
      color: #000;
    }
    .two {
      gap: 12pt;
    }
    section,
    tr {
      break-inside: avoid;
    }
    .foot {
      margin-top: 6pt;
      border-top: 0.5pt solid #999;
    }
  }
</style>
